import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { organizationApi } from "@/lib/api/services/organization.api";
import { uploadApi } from "@/lib/api/services/upload.api";
import { getApiErrorMessage } from "@/lib/api/api";

/**
 * Settings → Configuration → Branding (T-1301).
 *
 * A logo and two colours, shown where the organization's food is presented:
 * the recipe viewer, the cook mode header, and printed labels. Deliberately
 * not a theming system — the app around those surfaces stays the app.
 *
 * Unset means the neutral default, so "Reset" sends explicit nulls; the
 * surfaces render exactly what they rendered before branding existed.
 */

const DEFAULT_PICKER_COLOR = "#1f2937";

interface BrandingSettingsCardProps {
  /** False renders read-only — branding is an admin decision. */
  canManage: boolean;
}

function ColorField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
}) {
  const { t } = useLingui();
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={label}
          value={value ?? DEFAULT_PICKER_COLOR}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-9 w-14 cursor-pointer rounded-md border border-(--color-border) bg-(--color-surface-page) p-1 disabled:cursor-not-allowed"
        />
        <span className="w-20 font-mono text-xs text-(--color-text-secondary)">
          {value ?? t`Not set`}
        </span>
        {value !== null && !disabled && (
          <Button variant="ghost" size="icon" className="size-7" onClick={() => onChange(null)}>
            <X className="size-4" />
            <span className="sr-only">{t`Clear`}</span>
          </Button>
        )}
      </div>
    </div>
  );
}

export function BrandingSettingsCard({ canManage }: BrandingSettingsCardProps) {
  const { t } = useLingui();
  const { user, refetchUser } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const organization = user?.organization;

  // Drafts start from the session's organization and live here until saved.
  const [logoUrl, setLogoUrl] = useState<string | null>(organization?.logoUrl ?? null);
  const [primary, setPrimary] = useState<string | null>(organization?.brandPrimaryColor ?? null);
  const [secondary, setSecondary] = useState<string | null>(
    organization?.brandSecondaryColor ?? null,
  );
  const [isUploading, setIsUploading] = useState(false);

  const save = useMutation({
    mutationFn: (data: {
      logoUrl: string | null;
      brandPrimaryColor: string | null;
      brandSecondaryColor: string | null;
    }) => organizationApi.updateBranding(organization!.id, data),
    onSuccess: async () => {
      // The surfaces read branding off /auth/me, so refresh the session.
      await refetchUser();
      toast.success(t`Branding saved`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t`Could not save branding`)),
  });

  if (!organization) return null;

  const dirty =
    logoUrl !== (organization.logoUrl ?? null) ||
    primary !== (organization.brandPrimaryColor ?? null) ||
    secondary !== (organization.brandSecondaryColor ?? null);

  const handleLogoFile = async (file: File) => {
    setIsUploading(true);
    try {
      const uploaded = await uploadApi.uploadImage("organization", file);
      setLogoUrl(uploaded.imageUrl);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Could not upload the logo`));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t`Branding`}</CardTitle>
        <CardDescription>
          {t`Your logo and colours, shown on the recipe viewer, in cook mode, and on printed labels. Leave unset for the neutral default.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {/* Logo */}
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">{t`Logo`}</span>
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={t`Organization logo`}
                className="h-10 max-w-40 rounded-md border border-(--color-border) object-contain px-1"
              />
            ) : (
              <div className="flex h-10 w-16 items-center justify-center rounded-md border border-dashed border-(--color-border)">
                <ImageIcon className="size-4 text-(--color-text-secondary)" />
              </div>
            )}
            {canManage && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isUploading}
                  onClick={() => inputRef.current?.click()}
                  className="gap-2"
                >
                  {isUploading && <Loader2 className="size-4 animate-spin" />}
                  {logoUrl ? t`Replace` : t`Upload`}
                </Button>
                {logoUrl && (
                  <Button variant="ghost" size="sm" onClick={() => setLogoUrl(null)}>
                    {t`Remove`}
                  </Button>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleLogoFile(file);
                    e.target.value = "";
                  }}
                />
              </>
            )}
          </div>
        </div>

        <ColorField
          label={t`Primary colour`}
          value={primary}
          onChange={setPrimary}
          disabled={!canManage}
        />
        <ColorField
          label={t`Secondary colour`}
          value={secondary}
          onChange={setSecondary}
          disabled={!canManage}
        />

        {canManage && (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={save.isPending || (!logoUrl && !primary && !secondary)}
              onClick={() => {
                setLogoUrl(null);
                setPrimary(null);
                setSecondary(null);
              }}
            >
              {t`Reset to default`}
            </Button>
            <Button
              size="sm"
              disabled={!dirty || save.isPending || isUploading}
              onClick={() =>
                save.mutate({
                  logoUrl,
                  brandPrimaryColor: primary,
                  brandSecondaryColor: secondary,
                })
              }
              className="gap-2"
            >
              {save.isPending && <Loader2 className="size-4 animate-spin" />}
              {t`Save branding`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
